// Settings — widget toggles + live-support config. GET/PUT /settings. Plus time
// tracking prefs, pluggable time connectors, and a team rates editor.
import { useEffect, useState } from 'react';
import type { AgencySettings, AgencyUser, TimeProvider } from '../../lib/suite-types';
import type { ProductTier, TierFeature, TierInfo } from '../../lib/suite-types';
import { TIER_ORDER, TIER_META, TIER_ENTITLEMENTS, tierEntitled } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, Chip, ErrorNote, SectionHeader, Select, Spinner, Toggle } from './ui';
import { ThemePicker } from '../../components/ThemePicker';
import { applyTheme, getTheme, THEMES as CONSOLE_THEMES, type ThemeId } from '../../themes';

const DEFAULTS: AgencySettings = {
  widget_enabled: true,
  widget_docs_enabled: true,
  widget_videos_enabled: true,
  widget_tours_enabled: true,
  widget_tickets_enabled: true,
  widget_plans_enabled: true,
  widget_ftux_enabled: false,
  widget_position: 'bottom-right',
  widget_theme: 'dark',
  upgrade_url: null,
  default_billable: true,
  subaccount_url_template: null,
  live_support_mode: null,
  live_support_url: null,
  coverage_mode: 'grey_now',
  staleness_mode: 'branded_wins',
  brand_docs: true,
  brand_screenshots: true,
  white_videos: false,
  agency_theme: 'console',
  elevenlabs_voice_id: null,
  elevenlabs_key_set: false,
  voice_allowed: false,
  common_voice_id: null,
};

// Appearance — choose the console look. Applies instantly (optimistic) and saves on its own;
// on a failed save the previous theme is restored so the screen never lies about what's stored.
function AppearanceCard({ value, onSaved }: { value: ThemeId; onSaved: (id: ThemeId) => void }) {
  const [current, setCurrent] = useState<ThemeId>(value);
  const [note, setNote] = useState<string | null>(null);

  async function choose(id: ThemeId) {
    if (id === current) return;
    const previous = current;
    setCurrent(id);
    applyTheme(id);
    setNote(null);
    try {
      await agencyApi.saveTheme(id);
      onSaved(id);
      setNote(`Theme changed to ${CONSOLE_THEMES[id].meta.name}.`);
    } catch {
      setCurrent(previous);
      applyTheme(previous);
      setNote('Could not save the theme — reverted.');
    }
  }

  return (
    <Card className="p-4">
      <ThemePicker value={current} onSelect={choose} />
      {note && <p className="mt-3 text-xs text-muted">{note}</p>}
    </Card>
  );
}

const POSITIONS: [string, string][] = [
  ['bottom-right', 'Bottom right'],
  ['bottom-left', 'Bottom left'],
  ['top-right', 'Top right'],
  ['top-left', 'Top left'],
];

const LIVE_MODES: [string, string][] = [
  ['', 'Off'],
  ['chat', 'Live chat'],
  ['link', 'External link'],
];

type ThemeDef = { id: 'dark' | 'light'; label: string; header: string; icon: string };
// dark = accent header + white icon · light = light header + dark icon.
const THEMES: ThemeDef[] = [
  { id: 'dark', label: 'Dark', header: 'var(--ss-accent, #2563eb)', icon: '#ffffff' },
  { id: 'light', label: 'Light', header: '#f1f5f9', icon: '#1e293b' },
];

// Tiny visual preview of the widget launcher for a theme (accent/light header + icon dot).
function ThemeSwatch({
  theme,
  selected,
  onSelect,
}: {
  theme: ThemeDef;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      title={`${theme.label} theme`}
      className={`flex flex-col items-center gap-1 rounded-lg border p-1.5 transition ${
        selected ? 'border-brand ring-1 ring-brand' : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <span
        className="grid h-9 w-9 place-items-center rounded-full border border-black/5"
        style={{ backgroundColor: theme.header }}
      >
        <span className="h-4 w-4 rounded-full" style={{ backgroundColor: theme.icon }} />
      </span>
      <span className="text-xs font-medium text-slate-600">{theme.label}</span>
    </button>
  );
}

const WIDGET_TOGGLES: [keyof AgencySettings, string, string][] = [
  ['widget_docs_enabled', 'Docs', 'Show help articles in the widget'],
  ['widget_videos_enabled', 'Videos', 'Show tutorial videos'],
  ['widget_tours_enabled', 'Tours', 'Show guided product tours'],
  ['widget_tickets_enabled', 'Help desk', 'Let users submit & track support tickets'],
  ['widget_plans_enabled', 'Plans', 'Show plan upgrade nudges'],
  ['widget_ftux_enabled', 'Welcome tour (FTUX)', "Auto-run a first-time welcome tour on each user's first visit"],
];

// Which product-tier feature each widget toggle depends on. The master switch
// (widget_enabled) maps to 'widget'; the sub-toggles map to their own feature.
const TOGGLE_FEATURE: Partial<Record<keyof AgencySettings, TierFeature>> = {
  widget_enabled: 'widget',
  widget_docs_enabled: 'docs',
  widget_videos_enabled: 'videos',
  widget_tours_enabled: 'tours',
  widget_tickets_enabled: 'helpdesk',
  widget_plans_enabled: 'plans',
  widget_ftux_enabled: 'tours',
};

// Friendly labels for the tier's "what's included" one-liner.
const FEATURE_LABEL: Record<TierFeature, string> = {
  docs: 'Docs',
  videos: 'Videos',
  widget: 'Support widget',
  helpdesk: 'Help desk',
  tours: 'Tours',
  plans: 'Plan nudges',
  whatsnew: "What's new",
  time_tracking: 'Time tracking',
  context_help: 'Context help',
  admin_tracks: 'Admin tracks',
  custom_domain: 'Custom domain',
};

// Lowest tier (in TIER_ORDER) whose entitlements include a feature, or null.
function lowestTierFor(feature: TierFeature): ProductTier | null {
  return TIER_ORDER.find((t) => TIER_ENTITLEMENTS[t].includes(feature)) ?? null;
}

// Muted "Requires <Tier>" hint for a feature the current tier doesn't include.
function RequiresNote({ feature }: { feature: TierFeature }) {
  const need = lowestTierFor(feature);
  if (!need) return null;
  return (
    <div className="mt-0.5 text-xs text-slate-400">Requires {TIER_META[need].name}</div>
  );
}

export default function Settings() {
  const [s, setS] = useState<AgencySettings | null>(null);
  const [tier, setTier] = useState<TierInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    agencyApi
      .settings()
      .then((v) => setS(v ?? DEFAULTS))
      .catch((e: Error) => setError(e.message));
    agencyApi
      .getTier()
      .then(setTier)
      .catch((e: Error) => setError(e.message));
  }, []);

  function set<K extends keyof AgencySettings>(key: K, value: AgencySettings[K]) {
    setS((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
    setSaved(false);
  }

  async function save() {
    if (!s) return;
    setSaving(true);
    setError(null);
    try {
      await agencyApi.saveSettings(s);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (error) return <ErrorNote error={error} />;
  if (!s) return <Spinner />;

  return (
    <div className="max-w-2xl space-y-6">
      <SectionHeader
        title="Appearance"
        subtitle="The look of this console. Your brand color stays yours in every style."
      />
      <AppearanceCard
        value={getTheme(s.agency_theme).id}
        onSaved={(id) => setS((prev) => (prev ? { ...prev, agency_theme: id } : prev))}
      />

      <SectionHeader
        title="Widget settings"
        subtitle="Control what the embedded support widget shows on your clients' sub-accounts."
        action={
          <div className="flex items-center gap-3">
            {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
            <Button onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        }
      />

      {tier && (
        <Card className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div>
            <div className="flex items-center gap-2">
              <Chip label={`${TIER_META[tier.tier].name} · $${TIER_META[tier.tier].price}/mo`} />
              <span className="text-sm font-medium text-slate-800">Your plan</span>
            </div>
            <div className="mt-1.5 text-sm text-slate-500">
              Includes {tier.features.map((f) => FEATURE_LABEL[f]).join(', ')}.
            </div>
          </div>
          {tier.upgradeTarget && (
            <div className="text-right text-xs text-slate-400">
              Next: {tier.upgradeTarget.name} · ${tier.upgradeTarget.price}/mo
            </div>
          )}
        </Card>
      )}

      <Card className="divide-y divide-slate-100">
        {(() => {
          const masterFeature = TOGGLE_FEATURE.widget_enabled!;
          const masterLocked = tier != null && !tierEntitled(tier.tier, masterFeature);
          return (
            <div className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium text-slate-800">Support widget</div>
                <div className="text-sm text-slate-500">Master switch — turns the widget on for all clients.</div>
                {masterLocked && <RequiresNote feature={masterFeature} />}
              </div>
              <Toggle
                checked={s.widget_enabled}
                onChange={(v) => set('widget_enabled', v)}
                disabled={masterLocked}
              />
            </div>
          );
        })()}

        {WIDGET_TOGGLES.map(([key, label, hint]) => {
          const feature = TOGGLE_FEATURE[key];
          const locked = tier != null && feature != null && !tierEntitled(tier.tier, feature);
          return (
            <div key={key} className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium text-slate-800">{label}</div>
                <div className="text-sm text-slate-500">{hint}</div>
                {locked && feature && <RequiresNote feature={feature} />}
              </div>
              <Toggle
                checked={Boolean(s[key])}
                onChange={(v) => set(key, v as AgencySettings[typeof key])}
                disabled={!s.widget_enabled || locked}
              />
            </div>
          );
        })}

        <div className="flex items-center justify-between p-4">
          <div>
            <div className="font-medium text-slate-800">Widget position</div>
            <div className="text-sm text-slate-500">Corner the launcher docks to.</div>
          </div>
          <Select value={s.widget_position} onChange={(v) => set('widget_position', v)}>
            {POSITIONS.map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </Select>
        </div>

        <div className="flex items-center justify-between gap-4 p-4">
          <div>
            <div className="font-medium text-slate-800">Widget theme</div>
            <div className="text-sm text-slate-500">Header + launcher color the widget shows.</div>
          </div>
          <div className="flex gap-2">
            {THEMES.map((t) => (
              <ThemeSwatch
                key={t.id}
                theme={t}
                selected={s.widget_theme === t.id}
                onSelect={() => set('widget_theme', t.id)}
              />
            ))}
          </div>
        </div>
      </Card>

      <Card className="space-y-3 p-4">
        <div>
          <div className="font-medium text-slate-800">Upgrade URL</div>
          <div className="text-sm text-slate-500">
            Where locked-feature nudges send sub-account owners to self-upgrade.
          </div>
        </div>
        <input
          value={s.upgrade_url ?? ''}
          onChange={(e) => set('upgrade_url', e.target.value || null)}
          placeholder="https://… (your plan / billing upgrade page)"
          className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
      </Card>

      <Card className="space-y-4 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-slate-800">Billable by default</div>
            <div className="text-sm text-slate-500">New time entries start marked billable.</div>
          </div>
          <Toggle checked={s.default_billable} onChange={(v) => set('default_billable', v)} />
        </div>
        <div>
          <div className="font-medium text-slate-800">Sub-account URL template</div>
          <div className="text-sm text-slate-500">
            Used to build the “open in sub-account” hotlink when no captured page URL exists.
            Tokens: <code className="rounded bg-slate-100 px-1">{'{location_id}'}</code> ·{' '}
            <code className="rounded bg-slate-100 px-1">{'{route}'}</code>.
          </div>
          <input
            value={s.subaccount_url_template ?? ''}
            onChange={(e) => set('subaccount_url_template', e.target.value || null)}
            placeholder="https://app.example.com/v2/location/{location_id}/{route}"
            className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </div>
      </Card>

      <Card className="space-y-4 p-4">
        <div>
          <div className="font-medium text-slate-800">Content delivery</div>
          <div className="text-sm text-slate-500">
            How your clients' videos, docs, and screenshots get branded. Saved with your other settings.
          </div>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-medium text-slate-800">Video coverage</div>
            <div className="text-sm text-slate-500">
              What plays when you don't have a branded (white-label) video yet.
            </div>
          </div>
          <Select value={s.coverage_mode} onChange={(v) => set('coverage_mode', v as AgencySettings['coverage_mode'])}>
            <option value="grey_now">Show the neutral version now</option>
            <option value="white_only">Only show branded videos</option>
          </Select>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-medium text-slate-800">When we update a video</div>
            <div className="text-sm text-slate-500">
              If your branded version is behind our latest, which one your clients see.
            </div>
          </div>
          <Select value={s.staleness_mode} onChange={(v) => set('staleness_mode', v as AgencySettings['staleness_mode'])}>
            <option value="branded_wins">Keep showing my branded version</option>
            <option value="current_wins">Show the newest (neutral) version</option>
          </Select>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
          <div>
            <div className="font-medium text-slate-800">Brand the docs</div>
            <div className="text-sm text-slate-500">Substitute your name/logo into help articles.</div>
          </div>
          <Toggle checked={s.brand_docs} onChange={(v) => set('brand_docs', v)} />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-slate-800">Brand the screenshots</div>
            <div className="text-sm text-slate-500">Use your re-skinned screenshots where available.</div>
          </div>
          <Toggle checked={s.brand_screenshots} onChange={(v) => set('brand_screenshots', v)} />
        </div>
      </Card>

      <BrandedVideos />

      <VoiceSettings
        voiceAllowed={!!s.voice_allowed}
        commonVoiceId={s.common_voice_id ?? null}
        initialVoiceId={s.elevenlabs_voice_id ?? ''}
        keySetInitial={!!s.elevenlabs_key_set}
      />

      <TimeConnectors />
      <TeamRates />

      <Card className="space-y-4 p-4">
        <div>
          <div className="font-medium text-slate-800">Live support</div>
          <div className="text-sm text-slate-500">Offer a live-chat escalation beyond self-serve tickets.</div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={s.live_support_mode ?? ''} onChange={(v) => set('live_support_mode', v || null)}>
            {LIVE_MODES.map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </Select>
          {s.live_support_mode && (
            <input
              value={s.live_support_url ?? ''}
              onChange={(e) => set('live_support_url', e.target.value || null)}
              placeholder="https://… (chat embed or link URL)"
              className="min-w-[16rem] flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
          )}
        </div>
      </Card>
    </div>
  );
}

// ── Time connectors ────────────────────────────────────────────────
// Enable Clockify mirroring + workspace/project config, saved via /time/providers.
// Built pluggable — other providers drop into the same PUT payload later.
function TimeConnectors() {
  const [providers, setProviders] = useState<TimeProvider[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    agencyApi
      .timeProviders()
      .then(setProviders)
      .catch((e: Error) => setError(e.message));
  }, []);

  const clockify: TimeProvider =
    providers?.find((p) => p.provider === 'clockify') ?? { provider: 'clockify', enabled: false, config: null };
  const cfg = (clockify.config ?? {}) as Record<string, unknown>;
  const strVal = (k: string) => (typeof cfg[k] === 'string' ? (cfg[k] as string) : '');

  function updateClockify(patch: Partial<TimeProvider>, cfgPatch?: Record<string, unknown>) {
    setProviders((prev) => {
      const list = prev ? [...prev] : [];
      const idx = list.findIndex((p) => p.provider === 'clockify');
      const base = idx >= 0 ? list[idx] : clockify;
      const merged: TimeProvider = {
        ...base,
        ...patch,
        config: cfgPatch ? { ...(base.config ?? {}), ...cfgPatch } : base.config,
      };
      if (idx >= 0) list[idx] = merged;
      else list.push(merged);
      return list;
    });
    setDirty(true);
    setSaved(false);
  }

  async function save() {
    if (!providers) return;
    setSaving(true);
    setError(null);
    try {
      const next = await agencyApi.saveTimeProviders(providers);
      setProviders(next);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-medium text-slate-800">Time connectors</div>
          <div className="text-sm text-slate-500">
            Mirror stopped time entries to an external tracker. Native D1 tracking is always on.
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
          <Button onClick={save} disabled={!dirty || saving || !providers}>
            {saving ? 'Saving…' : 'Save connectors'}
          </Button>
        </div>
      </div>

      {error && <ErrorNote error={error} />}
      {!providers && !error ? (
        <Spinner label="Loading connectors…" />
      ) : (
        <div className="rounded-lg border border-slate-200">
          <div className="flex items-center justify-between p-3">
            <div>
              <div className="font-medium text-slate-800">Clockify</div>
              <div className="text-sm text-slate-500">Push entries on stop (needs a workspace API key on the server).</div>
            </div>
            <Toggle checked={clockify.enabled} onChange={(v) => updateClockify({ enabled: v })} />
          </div>
          {clockify.enabled && (
            <div className="grid gap-3 border-t border-slate-100 p-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Workspace ID</span>
                <input
                  value={strVal('workspaceId')}
                  onChange={(e) => updateClockify({}, { workspaceId: e.target.value })}
                  placeholder="Clockify workspace ID"
                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Project ID</span>
                <input
                  value={strVal('projectId')}
                  onChange={(e) => updateClockify({}, { projectId: e.target.value })}
                  placeholder="Default project ID (optional)"
                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
              </label>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Branded videos (whiten) ────────────────────────────────────────
// Turn tutorial videos into the agency's own branded versions. A "record" fan-out is a real,
// slow background job per video (Playwright re-record with brand injection) — so every action
// states its exact scope + cost before it runs. Voice-only changes re-narrate cheaply.
function BrandedVideos() {
  const [est, setEst] = useState<import('../../lib/agencyApi').WhitenEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'record' | 'revoice' | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  async function load() {
    try { setEst(await agencyApi.whitenEstimate()); } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void load(); }, []);

  async function run(mode: 'record' | 'revoice') {
    setBusy(true); setError(null); setDone(null);
    try {
      const r = await agencyApi.whiten(mode);
      const n = mode === 'record' ? (r.created + r.requeued) : r.requeued;
      setDone(
        mode === 'record'
          ? `Queued ${n} branded video${n === 1 ? '' : 's'} to record. We'll notify you as they're ready.`
          : `Queued ${n} video${n === 1 ? '' : 's'} to re-voice.`,
      );
      setConfirm(null);
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  if (error && !est) return <Card className="p-4"><ErrorNote error={error} /></Card>;
  if (!est) return <Card className="p-4"><Spinner label="Loading branded videos…" /></Card>;

  if (!est.entitled) {
    return (
      <Card className="space-y-1 p-4">
        <div className="font-medium text-slate-800">Branded videos</div>
        <div className="text-sm text-slate-500">
          Record your tutorial videos with your own logo &amp; branding. Available on plans that include videos (Starter and up).
        </div>
      </Card>
    );
  }

  const confirmCopy =
    confirm === 'record'
      ? {
          title: est.active ? 'Re-record with your current branding?' : 'Activate branded videos?',
          body: `This produces ${est.recordTargets} branded video${est.recordTargets === 1 ? '' : 's'} — each a fresh screen recording with your logo &amp; branding, made in the background. It can take a while; you'll be notified as they're ready. Until each one is done, your clients keep seeing the current version.`,
          cta: est.active ? 'Re-record all' : `Record ${est.recordTargets} videos`,
        }
      : {
          title: 'Re-voice your branded videos?',
          body: `This re-narrates your ${est.revoiceTargets} existing branded video${est.revoiceTargets === 1 ? '' : 's'} in your current voice. No re-recording — it's quick.`,
          cta: `Re-voice ${est.revoiceTargets} videos`,
        };

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800">Branded videos</span>
            {est.active ? <Chip label="On" color="#059669" /> : <Chip label="Off" muted />}
          </div>
          <div className="text-sm text-slate-500">
            Your tutorial videos, re-recorded with your logo &amp; branding. Branded versions replace the neutral ones for your clients as they finish.
          </div>
        </div>
      </div>

      {est.active && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Chip label={`${est.ready} ready`} color="#059669" />
          {est.inProgress > 0 && <Chip label={`${est.inProgress} in progress`} color="#0369a1" />}
          {est.failed > 0 && <Chip label={`${est.failed} failed`} color="#b91c1c" />}
        </div>
      )}

      {done && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{done}</p>}
      {error && <ErrorNote error={error} />}

      {confirm ? (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="text-sm font-medium text-slate-800">{confirmCopy.title}</div>
          <p className="text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: confirmCopy.body }} />
          <div className="flex items-center gap-2">
            <Button onClick={() => run(confirm)} disabled={busy}>{busy ? 'Queuing…' : confirmCopy.cta}</Button>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {!est.active ? (
            <Button onClick={() => setConfirm('record')} disabled={est.recordTargets === 0}>
              Activate branded videos{est.recordTargets ? ` (${est.recordTargets})` : ''}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setConfirm('record')}>Re-record with current branding</Button>
              {est.revoiceTargets > 0 && <Button variant="ghost" onClick={() => setConfirm('revoice')}>Re-voice only</Button>}
            </>
          )}
        </div>
      )}
      {est.recordTargets === 0 && !est.active && (
        <p className="text-xs text-slate-400">No recordable tutorial videos are available to brand yet.</p>
      )}
    </Card>
  );
}

// ── Voice (BYO ElevenLabs) ─────────────────────────────────────────
// Pro/Scale agencies can supply their own ElevenLabs key + voice for branded narration. The key
// is validated against ElevenLabs at save time, stored encrypted server-side, and NEVER returned
// (we only ever learn whether one is set). Its own save — separate from the main settings save.
function VoiceSettings({
  voiceAllowed, commonVoiceId, initialVoiceId, keySetInitial,
}: { voiceAllowed: boolean; commonVoiceId: string | null; initialVoiceId: string; keySetInitial: boolean }) {
  const [keyInput, setKeyInput] = useState('');
  const [voiceId, setVoiceId] = useState(initialVoiceId);
  const [keySet, setKeySet] = useState(keySetInitial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (!voiceAllowed) {
    return (
      <Card className="space-y-1 p-4">
        <div className="font-medium text-slate-800">Custom voice</div>
        <div className="text-sm text-slate-500">
          Bring your own ElevenLabs voice for narrated videos. Available on the Pro and Scale plans.
        </div>
      </Card>
    );
  }

  const dirty = keyInput.trim() !== '' || voiceId !== initialVoiceId;

  async function save() {
    setBusy(true); setError(null); setSaved(false);
    try {
      const body: { elevenlabs_key?: string; elevenlabs_voice_id?: string | null } = { elevenlabs_voice_id: voiceId.trim() || null };
      if (keyInput.trim()) body.elevenlabs_key = keyInput.trim();
      const r = await agencyApi.saveVoice(body);
      setKeySet(!!r.elevenlabs_key_set);
      setVoiceId(r.elevenlabs_voice_id ?? '');
      setKeyInput('');
      setSaved(true);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }
  async function removeKey() {
    setBusy(true); setError(null); setSaved(false);
    try {
      const r = await agencyApi.saveVoice({ elevenlabs_key: '' });
      setKeySet(!!r.elevenlabs_key_set);
      setVoiceId(r.elevenlabs_voice_id ?? '');
      setKeyInput('');
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-medium text-slate-800">Custom voice (ElevenLabs)</div>
          <div className="text-sm text-slate-500">
            Narrate your branded videos in your own voice. We verify the key with ElevenLabs before saving and
            store it encrypted — it's never shown again.
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
          <Button onClick={save} disabled={!dirty || busy}>{busy ? 'Verifying…' : 'Save voice'}</Button>
        </div>
      </div>

      {error && <ErrorNote error={error} />}

      <div>
        <div className="mb-1 flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">API key</span>
          {keySet && <Chip label="Key saved" color="#059669" />}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="password"
            value={keyInput}
            onChange={(e) => { setKeyInput(e.target.value); setSaved(false); }}
            placeholder={keySet ? 'Enter a new key to replace the saved one' : 'Paste your ElevenLabs API key'}
            autoComplete="off"
            className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
          {keySet && <Button variant="ghost" onClick={removeKey} disabled={busy}>Remove</Button>}
        </div>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Voice ID</div>
        <input
          value={voiceId}
          onChange={(e) => { setVoiceId(e.target.value); setSaved(false); }}
          placeholder="ElevenLabs voice ID (e.g. from your Voices library)"
          className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
        <p className="mt-1 text-xs text-slate-400">
          Leave blank to use the platform voice{commonVoiceId ? '' : ''}. We check the voice exists on your account when you save.
        </p>
      </div>
    </Card>
  );
}

// ── Team & rates ───────────────────────────────────────────────────
// List agency users; edit each one's billing rate (dollars → rate_cents) and
// active flag, saved per-row via PATCH /agency-users/:id.
function TeamRates() {
  const [users, setUsers] = useState<AgencyUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rateDraft, setRateDraft] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    agencyApi
      .agencyUsers()
      .then((list) => {
        setUsers(list);
        setRateDraft(
          Object.fromEntries(list.map((u) => [u.id, u.rate_cents != null ? (u.rate_cents / 100).toFixed(2) : ''])),
        );
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const dollarsToCents = (v: string): number | null => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };

  const rowDirty = (u: AgencyUser) => {
    const cents = dollarsToCents(rateDraft[u.id] ?? '');
    return cents !== (u.rate_cents ?? null);
  };

  async function saveRate(u: AgencyUser) {
    setSavingId(u.id);
    setError(null);
    try {
      const rate_cents = dollarsToCents(rateDraft[u.id] ?? '');
      const updated = await agencyApi.patchAgencyUser(u.id, { rate_cents });
      setUsers((prev) => (prev ? prev.map((x) => (x.id === u.id ? { ...x, ...updated } : x)) : prev));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingId(null);
    }
  }

  async function toggleActive(u: AgencyUser, active: boolean) {
    setUsers((prev) => (prev ? prev.map((x) => (x.id === u.id ? { ...x, active } : x)) : prev));
    try {
      await agencyApi.patchAgencyUser(u.id, { active });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Card className="space-y-4 p-4">
      <div>
        <div className="font-medium text-slate-800">Team &amp; rates</div>
        <div className="text-sm text-slate-500">
          Hourly billing rate per agent — drives billable value in the Time report.
        </div>
      </div>

      {error && <ErrorNote error={error} />}
      {!users && !error ? (
        <Spinner label="Loading team…" />
      ) : users && users.length === 0 ? (
        <p className="text-sm text-slate-400">No team members yet.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {users?.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-800">{u.name}</div>
                <div className="truncate text-xs text-slate-400">{u.email ?? u.role}</div>
              </div>
              <Toggle checked={u.active} onChange={(v) => toggleActive(u, v)} label="Active" />
              <div className="flex items-center gap-1">
                <span className="text-sm text-slate-400">$</span>
                <input
                  value={rateDraft[u.id] ?? ''}
                  onChange={(e) => setRateDraft((d) => ({ ...d, [u.id]: e.target.value }))}
                  inputMode="decimal"
                  placeholder="0.00"
                  className="w-24 rounded-lg border border-slate-200 px-3 py-1.5 text-right text-sm tabular-nums outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
                <span className="text-xs text-slate-400">/hr</span>
              </div>
              <Button
                variant="secondary"
                onClick={() => saveRate(u)}
                disabled={savingId === u.id || !rowDirty(u)}
              >
                {savingId === u.id ? 'Saving…' : 'Save'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
